import React, {
  DetailedHTMLProps,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import classNames from "classnames";
import SearchIcon from "@mui/icons-material/Search";
import { useSearchSuggestionsByTitle } from "../../../hooks";
import { Suggestion } from "../../../apis/search-catalog";

export interface SearchInputProps
  extends DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  className?: string;
  onClickSearch: any;
  onClickSearchSuggestion?: (suggestion: Suggestion) => void;
}

export const SearchBar = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      className,
      onClickSearch,
      onClickSearchSuggestion,
      placeholder = "Buscar...",
      ...inputProps
    },
    ref
  ) => {
    const [value, setValue] = useState("");
    const [debouncedValue, setDebouncedValue] = useState(value);
    const wrapperClassName = useMemo(
      () =>
        classNames({
          "xvia-container-search-input": true,
          [className as string]: !!className,
        }),
      [className]
    );

    const { data: suggestions = [] } = useSearchSuggestionsByTitle({
      value: debouncedValue,
    });

    useEffect(() => {
      const timeout = setTimeout(() => {
        setDebouncedValue(value);
      }, 500);

      return () => clearTimeout(timeout);
    }, [value]);

    const keyDownHandler = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
          event.preventDefault();
          onClickSearch(value);
        }
      },
      [onClickSearch, value]
    );

    const handleSuggestionClick = useCallback(
      (suggestion: Suggestion) => {
        setValue(suggestion.title);
        if (onClickSearchSuggestion) {
          onClickSearchSuggestion(suggestion);
          return;
        }
        onClickSearch(suggestion.title);
      },
      [onClickSearch, onClickSearchSuggestion]
    );

    const shouldShowSuggestions =
      debouncedValue.length >= 2 && suggestions.length > 0;

    return (
      <div className="xvia-search-input__wrapper">
        <div className={wrapperClassName}>
          <div className="xvia-search-input__block">
            <div className="xvia-search-input__inputContainer">
              <SearchIcon />
              <input
                ref={ref}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={keyDownHandler}
                className="xvia-search-input__input"
                value={value}
                type="text"
                name="keyword"
                aria-label="Campo de busca"
                placeholder={placeholder}
                {...inputProps}
              />
            </div>
            <div className="xvia-search-input__button-wrapper">
              <button onClick={() => onClickSearch(value)}>Buscar</button>
            </div>
          </div>
        </div>
        {shouldShowSuggestions && (
          <div className="xvia-search-input__suggestions">
            <ul className="xvia-search-input__suggestions-list">
              {suggestions.map((suggestion) => (
                <li
                  className="xvia-search-input__suggestions-item"
                  key={suggestion.slug}
                >
                  <button
                    type="button"
                    className="xvia-search-input__suggestions-button"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {highlightMatch(suggestion.title, debouncedValue)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
);

const highlightMatch = (text: string, query?: string) => {
  if (!query) return text;

  const normalizedText = normalize(text);
  const normalizedQuery = normalize(query);

  const startIndex = normalizedText.indexOf(normalizedQuery);

  if (startIndex === -1) return text;

  const endIndex = startIndex + normalizedQuery.length;

  return (
    <>
      {text.slice(0, startIndex)}
      <strong>{text.slice(startIndex, endIndex)}</strong>
      {text.slice(endIndex)}
    </>
  );
};

const normalize = (str: string) =>
  str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
